/**
 * Sub-aba "Preço Padrão Produto": o preço de cada um dos 8 produtos, que pré-preenche o valor total
 * no "Registrar pagamento". Porte de `admin.html:2076-2079` e `9293-9371`; linha 139 do inventário.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO: campo vazio grava ZERO (o `parseMoeda('')` do legado dá
 * 0), e o erro de salvar some em 3 s como o sucesso, porque o legado usa a mesma função para os dois.
 */

// Estado, memória e efeito do React.
import { useCallback, useEffect, useState } from 'react';
// O botao do Chassi: o unico Salvar desta tela.
import { Botao } from '@complexity/ui';
// As chamadas da área.
import { listarPrecosProdutos, salvarPrecoProduto } from '@studiolu/dados/areas/sistema';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O status que some sozinho em 3 s (`mostrarStatusParamPrecos`, `admin.html:9311`).
import { LinhaDeStatus, useStatusTemporario } from '../../comum/status-temporario.js';
// A moeda do legado: cada dígito entra como centavo, e o texto volta a número.
import { mascararMoedaLegado, parseMoeda } from '../../comum/moeda.js';
// A formatação idêntica à do legado.
import { formatarMoeda } from '../../comum/formatos.js';
// Os produtos e a referência de mercado, na ordem do legado.
import { PARAM_PRODUTOS_LABEL, SUGESTAO_PRECOS_MERCADO } from './catalogos.js';
// As peças visuais da área.
import {
  CabecalhoDeColunas,
  CartaoParam,
  LinhaParam,
  NotaSecao,
  TextoParam,
  type Carga,
  type PropsDaSubAba,
} from './pecas.js';

export function SubAbaPrecos({ recarga }: Pick<PropsDaSubAba, 'recarga'>) {
  const { dados } = usePainel();
  const [carga, setCarga] = useState<Carga<null>>({ tipo: 'carregando' });
  // O texto de cada campo, já com a máscara; nasce do preço cadastrado.
  const [textos, setTextos] = useState<Readonly<Record<string, string>>>({});
  /**
   * O QUE O BANCO TEM, guardado à parte do que está na tela.
   *
   * É a diferença entre os dois que diz o que precisa ser gravado. O mesmo desenho da sub-aba
   * "Visibilidade Aba", pelo mesmo motivo: sem o original, o botão único gravaria os treze produtos
   * a cada clique.
   */
  const [original, setOriginal] = useState<Readonly<Record<string, string>>>({});
  // Trava o botão durante a gravação: clicar duas vezes gravaria a mesma coisa duas vezes.
  const [salvando, setSalvando] = useState(false);
  const [status, mostrarStatus, fixarStatus] = useStatusTemporario();

  // `carregarPrecosProdutos` (`admin.html:9316-9327`).
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    // O status mora dentro da lista no legado, e some quando a lista é reescrita.
    fixarStatus('');
    const resultado = await listarPrecosProdutos(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    const precos: Record<string, number | null> = {};
    for (const linha of resultado.dados ?? []) precos[linha.produto] = linha.preco;
    // Preço cadastrado vira "R$ 1.234,56"; sem preço, o campo fica vazio (`admin.html:9347`).
    const naTela = Object.fromEntries(
      Object.keys(PARAM_PRODUTOS_LABEL).map((p) => {
        const preco = precos[p];
        return [p, preco !== null && preco !== undefined ? formatarMoeda(Number(preco)) : ''];
      }),
    );
    setTextos(naTela);
    // O mesmo retrato guardado como "o que o banco tem": daqui em diante, diferença é alteração.
    setOriginal(naTela);
    setCarga({ tipo: 'pronta', dados: null });
  }, [dados, fixarStatus]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  /**
   * OS PRODUTOS QUE MUDARAM, comparando o campo na tela com o que veio do banco.
   *
   * A comparação é do TEXTO já mascarado, e não do número: é o texto que a pessoa vê, e
   * "R$ 1.300,00" digitado por cima de "R$ 1.300,00" não é alteração nenhuma.
   */
  const alterados = Object.keys(PARAM_PRODUTOS_LABEL).filter(
    (produto) => (textos[produto] ?? '') !== (original[produto] ?? ''),
  );

  /**
   * "Salvar alterações": UM botão para a tela inteira, por pedido do fundador em 07/10/2026
   * ("mesma coisa, apenas um botao para salvar"), igual ao da sub-aba "Visibilidade Aba".
   *
   * MUDA O LEGADO DE PROPÓSITO, e ele decidiu: o admin atual tem um "Salvar" por linha
   * (`admin.html:9361-9370`), e ajustar três preços custava três cliques e três idas ao banco.
   *
   * Grava um de cada vez e PARA na primeira falha, nomeando o produto: continuar depois de um erro
   * deixaria a tela dizendo "salvo" com metade gravada.
   */
  const salvarTudo = useCallback(async () => {
    if (alterados.length === 0) return;
    setSalvando(true);
    fixarStatus('');
    let gravados = 0;
    for (const produto of alterados) {
      // Campo vazio vira 0, como o `parseMoeda` do legado.
      const preco = parseMoeda(textos[produto] ?? '');
      const resultado = await salvarPrecoProduto(dados, produto, preco, new Date().toISOString());
      if (!resultado.ok) {
        setSalvando(false);
        mostrarStatus(`Erro ao salvar "${PARAM_PRODUTOS_LABEL[produto]}": ${resultado.erro.mensagem}`);
        return;
      }
      // O original avança junto: o que já gravou sai da conta, mesmo se o próximo falhar.
      setOriginal((atuais) => ({ ...atuais, [produto]: textos[produto] ?? '' }));
      gravados++;
    }
    setSalvando(false);
    mostrarStatus(gravados === 1 ? 'Preço salvo.' : `${gravados} preços salvos.`);
  }, [alterados, textos, dados, mostrarStatus, fixarStatus]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Define o preço padrão de cada produto, usado pra pré-preencher o valor total no registro de pagamento do funil de
        vendas.
      </NotaSecao>
      {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
      {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
      {carga.tipo === 'pronta' && (
        <>
          <CartaoParam testid="lista-precos">
            <CabecalhoDeColunas>
              <span className="min-w-0 flex-1">Produto</span>
              <span className="w-[160px] shrink-0">Preço padrão</span>
              <span className="w-[76px] shrink-0" />
            </CabecalhoDeColunas>
            {Object.keys(PARAM_PRODUTOS_LABEL).map((produto) => (
              <LinhaParam key={produto} data-produto={produto}>
                <span className="min-w-0 basis-full text-cx-sm [overflow-wrap:anywhere] md:flex-1 md:basis-0">
                  {PARAM_PRODUTOS_LABEL[produto]}
                  {/* A referência de mercado em letra pequena, logo abaixo (`admin.html:9343`). */}
                  <span className="mt-cx-1 block text-cx-xs font-normal text-texto-suave">{SUGESTAO_PRECOS_MERCADO[produto] || ''}</span>
                </span>
                <span className="min-w-0 md:w-[160px] md:shrink-0">
                  {/* A caixa do Chassi (`cx-campo`), sem rótulo próprio: o nome dela é o cabeçalho
                      "Preço padrão" com o produto, dito ao leitor de tela, como a tabela do legado. */}
                  <input
                    type="text"
                    id={`preco_${produto}`}
                    aria-label={`Preço padrão de ${PARAM_PRODUTOS_LABEL[produto]}`}
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    className="cx-campo w-[130px]"
                    value={textos[produto] ?? ''}
                    onChange={(evento) => {
                      const valor = mascararMoedaLegado(evento.target.value);
                      setTextos((atuais) => ({ ...atuais, [produto]: valor }));
                    }}
                  />
                </span>
              </LinhaParam>
            ))}
          </CartaoParam>

          {/*
            UM BOTÃO PARA A TELA INTEIRA, no rodapé à direita, igual ao da sub-aba "Visibilidade
            Aba". Ele diz quantas alterações vai gravar, e fica desligado quando não há nenhuma.
          */}
          <div className="flex flex-wrap items-center justify-end gap-cx-3">
            <span className="text-cx-sm text-texto-suave" data-testid="contagem-precos-alterados">
              {alterados.length === 0
                ? 'Nenhuma alteração para salvar.'
                : alterados.length === 1
                  ? '1 alteração pendente.'
                  : `${alterados.length} alterações pendentes.`}
            </span>
            <Botao
              variante="primario"
              onClick={() => void salvarTudo()}
              disabled={alterados.length === 0 || salvando}
              data-testid="salvar-precos"
            >
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </Botao>
          </div>

          <LinhaDeStatus texto={status} testid="status-precos" />
        </>
      )}
    </div>
  );
}
